<script setup lang="ts">
/**
 * 单条消息气泡。
 *
 * 关于"要不要用 memo"：React 版靠 `memo` 防止每个 delta 把整段历史（最多 200 条）重渲。
 * Vue 的编译器给每个组件都生成了依赖追踪，父组件重渲不会连带重渲子组件——
 * 只要传进去的 props 本身没变。所以这里**不需要**任何手写优化，
 * 但前提是 `view` 是同一个对象引用：chat-store 只在真正改动的那条消息上做替换。
 *
 * `hasMeta` 里包含 `errorMessage` 是刻意的：请求阶段就失败（比如网络断了、
 * 服务端 50200）时 `latencyMs` 是 null，如果不算进去，
 * 用户看到一条报错却找不到「重新生成」按钮。
 */

import { computed } from 'vue';

import { cn } from '../../lib/cn';
import type { MessageViewModel } from '../../types';
import SafetyNotice from '../safety/SafetyNotice.vue';
import AssistantSkeleton from '../ui/AssistantSkeleton.vue';
import AgeBandBadge from './AgeBandBadge.vue';
import DomainBadge from './DomainBadge.vue';
import StreamingMarkdown from './StreamingMarkdown.vue';

const props = defineProps<{
  view: MessageViewModel;
  onRegenerate?: (() => void) | undefined;
  className?: string;
}>();

function formatLatency(ms: number): string {
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
  return `${ms}ms`;
}

const showSkeleton = computed(() => props.view.streaming === true && props.view.content.length === 0);

/** 整条被撤回：正文不再渲染，只留安全提示卡。 */
const isRetracted = computed(() => props.view.retracted === true);

const hasError = computed(
  () => props.view.errorMessage !== null && props.view.errorMessage !== undefined && props.view.errorMessage.length > 0,
);

const hasSafety = computed(
  () =>
    props.view.safetyMessage !== null &&
    props.view.safetyMessage !== undefined &&
    props.view.safetyMessage.length > 0,
);

/**
 * 「估算」标记：只有服务端**明确**说这一轮的数字是估算时才打。
 *
 * `estimated === null`（历史消息、或还没收到 `usage` 帧）和为 `false`（服务端给了
 * 实测值）都不打——把"不知道"渲染成一个"估算"标，等于凭空给用户一个结论。
 */
const showEstimate = computed(() => props.view.estimated === true);

/** 元信息行在 `hasMeta` 成立时才出现：修正见文件头（`errorMessage` 也是触发条件）。 */
const hasMeta = computed(
  () =>
    showEstimate.value ||
    props.view.latencyMs !== null ||
    props.view.aborted === true ||
    hasError.value,
);

const showRegenerate = computed(
  () =>
    props.onRegenerate !== undefined &&
    (props.view.aborted === true || hasError.value || isRetracted.value),
);
</script>

<template>
  <!-- 用户消息：右对齐、主色实心气泡。 -->
  <div v-if="props.view.role === 'user'" :class="cn('flex justify-end', props.className)">
    <div class="flex max-w-[85%] flex-col items-end gap-1">
      <div
        class="whitespace-pre-wrap break-words rounded-2xl rounded-br-sm bg-brand-500 px-4 py-2.5 text-[0.9375rem] leading-relaxed text-white"
      >
        {{ props.view.content }}
      </div>
      <div v-if="props.view.domain !== null || props.view.ageBand !== null" class="flex items-center gap-1">
        <DomainBadge v-if="props.view.domain !== null" :domain="props.view.domain" />
        <AgeBandBadge v-if="props.view.ageBand !== null" :age-band="props.view.ageBand" />
      </div>
    </div>
  </div>

  <!-- 助手消息：左对齐、白底描边卡片。 -->
  <div v-else :class="cn('flex justify-start', props.className)">
    <div
      class="w-full max-w-[92%] rounded-2xl rounded-bl-sm border border-slate-200 bg-white px-4 py-3 shadow-sm"
    >
      <div
        v-if="props.view.domain !== null || props.view.ageBand !== null"
        class="mb-1.5 flex flex-wrap items-center gap-1.5"
      >
        <DomainBadge v-if="props.view.domain !== null" :domain="props.view.domain" />
        <AgeBandBadge v-if="props.view.ageBand !== null" :age-band="props.view.ageBand" />
        <span v-if="props.view.streaming === true" class="text-[11px] text-slate-400" aria-hidden="true">
          正在生成…
        </span>
      </div>

      <AssistantSkeleton v-if="showSkeleton" />
      <StreamingMarkdown v-else-if="!isRetracted" :content="props.view.content" :streaming="props.view.streaming === true" />

      <p v-if="isRetracted" class="text-xs text-slate-500">
        这条回答已被撤回，未计入额度。
      </p>

      <p
        v-if="props.view.aborted === true"
        class="mt-2 inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600"
      >
        <span aria-hidden="true">■</span>已中止生成
      </p>

      <p
        v-if="hasError"
        class="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900"
      >
        {{ props.view.errorMessage }}
      </p>

      <SafetyNotice v-if="hasSafety" :message="props.view.safetyMessage ?? ''" />

      <div
        v-if="hasMeta"
        class="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-slate-100 pt-2 text-[11px] text-slate-500"
      >
        <span
          v-if="showEstimate"
          class="rounded bg-slate-100 px-1.5 py-0.5"
          title="这一轮的消耗为估算值"
        >
          估算
        </span>
        <span v-if="props.view.latencyMs !== null" class="tabular-nums">
          耗时 {{ formatLatency(props.view.latencyMs) }}
        </span>
        <span v-if="props.view.upstreamModel !== null" class="max-w-[12rem] truncate">
          {{ props.view.upstreamModel }}
        </span>
        <button
          v-if="showRegenerate"
          type="button"
          class="ml-auto rounded border border-slate-300 px-2 py-0.5 text-[11px] font-medium text-slate-700 hover:bg-slate-50"
          @click="props.onRegenerate?.()"
        >
          重新生成
        </button>
      </div>
    </div>
  </div>
</template>
